import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { CustomBreadcrumb, CustomModal, CustomInput, CustomButton } from '@/shared/components/atoms';
import { CustomCommonScreenHeader, CustomTable, CustomTableColumn, ConfirmationModal, ImportSummaryModal, ImportPreviewModal } from '@/shared/components/molecules';
import { Home, LayoutGrid, Pencil, Trash2, Download, Upload } from 'lucide-react';
import { departmentApi } from '../api/departmentApi';
import { Department } from '../types';
import { useToastStore } from '@/shared/stores/toastStore';
import { useAuthStore } from '@/shared/stores/authStore';

export const ListDepartments = () => {
    const navigate = useNavigate();
    const { user } = useAuthStore();
    const [departments, setDepartments] = useState<Department[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [hasNext, setHasNext] = useState(false);
    const [hasPrevious, setHasPrevious] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [departmentToDelete, setDepartmentToDelete] = useState<Department | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [departmentToEdit, setDepartmentToEdit] = useState<Department | null>(null);
    const [editName, setEditName] = useState('');
    const [isUpdating, setIsUpdating] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const [importFile, setImportFile] = useState<File | null>(null);
    const [isImportPreviewOpen, setIsImportPreviewOpen] = useState(false);

    // Import status modal state
    const [isImportSummaryOpen, setIsImportSummaryOpen] = useState(false);
    const [importSummaryData, setImportSummaryData] = useState<{
        success: number;
        failed: number;
        errors: string[];
        fileName: string;
    } | null>(null);

    const fileInputRef = useRef<HTMLInputElement>(null);
    const { showToast } = useToastStore();

    const schoolSlug = user?.school?.slug || '';

    const handleDownloadTemplate = async () => {
        if (!schoolSlug) return;
        setIsDownloading(true);
        try {
            const response = await departmentApi.downloadTemplate(schoolSlug, 'departments');
            if (response.success && response.data) {
                const contentDisposition = response.headers?.['content-disposition'];
                let fileName = 'departments_template.xlsx';
                if (contentDisposition) {
                    const match = contentDisposition.match(/filename=(.+)/);
                    if (match && match[1]) {
                        fileName = match[1].replace(/['"]/g, '');
                    }
                }
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                showToast('Template downloaded successfully', 'success');
            } else {
                showToast(response.error || 'Failed to download template', 'error');
            }
        } catch (error) {
            showToast('Failed to download template', 'error');
        } finally {
            setIsDownloading(false);
        }
    };

    const handleFileImport = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !schoolSlug) return;

        // Validate file extension
        const allowedExtensions = ['xlsx', 'xls', 'csv'];
        const fileExtension = file.name.split('.').pop()?.toLowerCase();
        
        if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
            showToast('Please upload only Excel (.xlsx, .xls) or CSV (.csv) files.', 'error');
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            return;
        }

        setImportFile(file);
        setIsImportPreviewOpen(true);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const fetchDepartments = useCallback(async () => {
        if (!schoolSlug) return;
        setIsLoading(true);
        try {
            const response = await departmentApi.getDepartments(schoolSlug, { page, page_size: pageSize });
            if (response.success && response.data) {
                const data = response.data as any;
                // Support both old (array) and new (paginated) shape
                if (Array.isArray(data)) {
                    setDepartments(data);
                    setTotalCount(data.length);
                    setTotalPages(1);
                    setHasNext(false);
                    setHasPrevious(false);
                } else {
                    setDepartments(data.items || []);
                    setTotalCount(data.total_count || 0);
                    setTotalPages(data.total_pages || 1);
                    setHasNext(data.has_next || false);
                    setHasPrevious(data.has_previous || false);
                }
            }
        } catch (error) {
            showToast('Failed to load departments', 'error');
        } finally {
            setIsLoading(false);
        }
    }, [schoolSlug, page, pageSize]);

    useEffect(() => {
        fetchDepartments();
    }, [fetchDepartments]);

    const handleEditClick = (dept: Department) => {
        setDepartmentToEdit(dept);
        setEditName(dept.name);
        setIsEditModalOpen(true);
    };

    const handleUpdateDepartment = async () => {
        if (!departmentToEdit || !editName.trim() || !schoolSlug) return;

        setIsUpdating(true);
        try {
            const response = await departmentApi.updateDepartment(schoolSlug, departmentToEdit.id, { name: editName.trim() });
            if (response.success) {
                showToast('Department updated successfully', 'success');
                fetchDepartments();
                setIsEditModalOpen(false);
            } else {
                showToast(response.error || 'Failed to update department', 'error');
            }
        } catch (error) {
            showToast('An unexpected error occurred', 'error');
        } finally {
            setIsUpdating(false);
        }
    };

    const handleDeleteClick = (dept: Department) => {
        setDepartmentToDelete(dept);
        setIsDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!departmentToDelete || !schoolSlug) return;

        setIsDeleting(true);
        try {
            const response = await departmentApi.deleteDepartment(schoolSlug, departmentToDelete.id);
            if (response.success) {
                showToast('Department deleted successfully', 'success');
                fetchDepartments();
                setIsDeleteModalOpen(false);
            } else {
                showToast(response.error || 'Failed to delete department', 'error');
            }
        } catch (error) {
            showToast('An unexpected error occurred', 'error');
        } finally {
            setIsDeleting(false);
        }
    };

    const columns: CustomTableColumn<Department>[] = [
        {
            header: 'Department Name',
            key: 'name',
            render: (dept) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <span style={{ fontWeight: '500' }}>{dept.name}</span>
                </div>
            )
        },
        {
            header: 'Created At',
            key: 'created_at',
            render: (dept) => (
                <span style={{ color: 'var(--text-secondary)' }}>
                    {new Date(dept.created_at).toLocaleDateString(undefined, {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                    })}
                </span>
            )
        },
        {
            header: 'Actions',
            key: 'actions',
            align: 'right',
            minWidth: '110px',
            width: '110px',
            render: (dept) => (
                <div className="table-action-cell" style={{ justifyContent: 'flex-end' }}>
                    <button
                        className="table-action-btn"
                        onClick={(e) => { e.stopPropagation(); handleEditClick(dept); }}
                        title="Edit Department"
                    >
                        <Pencil size={18} />
                    </button>
                    <button
                        className="table-action-btn danger"
                        onClick={(e) => { e.stopPropagation(); handleDeleteClick(dept); }}
                        title="Delete Department"
                    >
                        <Trash2 size={18} />
                    </button>
                </div>
            )
        }
    ];

    return (
        <div className="page-container">
            <CustomBreadcrumb
                items={[
                    { label: 'Home', path: '/', icon: <Home size={14} /> },
                    { label: 'Departments', icon: <LayoutGrid size={14} /> }
                ]}
            />
            <CustomCommonScreenHeader
                title="Department Management"
                subtitle="Organize your school by creating and managing departments."
                icon={<LayoutGrid size={24} />}
                actionLabel="Add Department"
                onActionClick={() => navigate(`/${schoolSlug}/school-admin/departments/create`)}
            />

            {/* Responsive Action Bar (Import / Download) */}
            <div className="table-filter-bar">
                <div className="table-filter-group">
                    <CustomButton
                        variant="outline"
                        size="small"
                        onClick={handleDownloadTemplate}
                        disabled={isDownloading}
                    >
                        <Download size={16} style={{ marginRight: '0.5rem' }} />
                        {isDownloading ? 'Downloading...' : 'Download Template'}
                    </CustomButton>
                    
                    <CustomButton
                        variant="outline"
                        size="small"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isImporting}
                    >
                        <Upload size={16} style={{ marginRight: '0.5rem' }} />
                        {isImporting ? 'Importing...' : 'Import Departments'}
                    </CustomButton>
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileImport}
                        accept=".xlsx, .xls, .csv"
                        style={{ display: 'none' }}
                    />
                </div>
            </div>

            <CustomTable
                columns={columns}
                data={departments}
                isLoading={isLoading}
                isCompact={true}
                emptyMessage="No departments found"
                emptyIcon={<LayoutGrid size={32} />}
                pagination={{
                    currentPage: page,
                    totalPages: totalPages,
                    totalCount: totalCount,
                    pageSize: pageSize,
                    onPageChange: setPage,
                    hasPrevious: hasPrevious,
                    hasNext: hasNext,
                    isLoading: isLoading,
                    itemName: 'departments',
                }}
            />

            <CustomModal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                title="Edit Department"
                footer={
                    <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', width: '100%' }}>
                        <button
                            onClick={() => setIsEditModalOpen(false)}
                            style={{ padding: '0.5rem 1rem', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'none', cursor: 'pointer' }}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleUpdateDepartment}
                            disabled={isUpdating || !editName.trim()}
                            style={{
                                padding: '0.5rem 1rem',
                                borderRadius: '8px',
                                border: 'none',
                                background: 'var(--accent-primary)',
                                color: 'white',
                                cursor: (isUpdating || !editName.trim()) ? 'not-allowed' : 'pointer',
                                opacity: (isUpdating || !editName.trim()) ? 0.7 : 1
                            }}
                        >
                            {isUpdating ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                }
            >
                <div style={{ padding: '1rem 0' }}>
                    <CustomInput
                        name="departmentName"
                        label="Department Name"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        placeholder="Enter department name"
                    />
                </div>
            </CustomModal>

            <ConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleConfirmDelete}
                title="Delete Department"
                message={`Are you sure you want to delete the department "${departmentToDelete?.name}"? This action cannot be undone.`}
                confirmLabel="Delete"
                variant="danger"
                isLoading={isDeleting}
            />

            {importSummaryData && (
                <ImportSummaryModal
                    isOpen={isImportSummaryOpen}
                    onClose={() => {
                        setIsImportSummaryOpen(false);
                        setImportSummaryData(null);
                    }}
                    successCount={importSummaryData.success}
                    failedCount={importSummaryData.failed}
                    errors={importSummaryData.errors}
                    fileName={importSummaryData.fileName}
                    title="Department Import Summary"
                />
            )}

            <ImportPreviewModal
                isOpen={isImportPreviewOpen}
                onClose={() => {
                    setIsImportPreviewOpen(false);
                    setImportFile(null);
                }}
                file={importFile}
                type="departments"
                onImport={(file) => departmentApi.importFile(schoolSlug, 'departments', file)}
                onSuccess={() => {
                    fetchDepartments();
                }}
            />
        </div>
    );
};
